/**
 * Links INTO filtered lists. Every count a tile shows ("3 child cards", "used
 * in 7 collections", "Cards: 12") is a door to the list of exactly those
 * records, and the door is a `?q=` KQL query on the list route. The query text
 * is produced by the grammar's own serializer over constructor-built nodes —
 * never by string concatenation — so what lands in the URL is canonical and
 * round-trips through the chips and the builder unchanged.
 */
import { cmp, serialize, str } from './kql';
import { normalizeId } from './ids';

/** `${base}?q=<encoded>` — or just `base` when the query is blank. */
export function listPath(base, queryText) {
	const trimmed = queryText === undefined || queryText === null ? '' : String(queryText).trim();
	if (!trimmed) return base;
	const params = new URLSearchParams();
	params.set('q', trimmed);
	return `${base}?${params.toString()}`;
}

/** One `<path> = "<value>"` condition as canonical text ('' without a value). */
const equalsText = (path, value) => (value ? serialize(cmp(path, 'eq', str(value))) : '');

/**
 * What a card ref condition carries: the key when the card has one (readable
 * in the URL, stable across environments), else its id. Accepts a card doc or
 * a bare id.
 */
export function cardRef(card) {
	if (!card) return '';
	if (typeof card === 'string') return card;
	return card.key || normalizeId(card._id) || '';
}

/** The cards list filtered to this card's direct children. */
export function childCardsPath(card) {
	return listPath('/cards', equalsText(['parent'], cardRef(card)));
}

/** The collections list filtered to the collections that use this card. */
export function cardCollectionsPath(card) {
	return listPath('/collections', equalsText(['card'], cardRef(card)));
}

/** The cards list filtered to the cards a collection uses. */
export function collectionCardsPath(collectionId) {
	return listPath('/cards', equalsText(['collection'], normalizeId(collectionId) || ''));
}

/**
 * A door the SERVER built (a KQL text, e.g. a driven element's "Try it on",
 * KPR-959) as the collection's item list: `/collections/:id/items?linked=0&q=`.
 * `linked=0` because the door counts the collection's own items, never linked
 * ones (knowledge: doors-to-a-filtered-list). Null without a door or a
 * well-formed collection id. The text is carried, never built or joined here.
 */
export function itemsDoorPath(collectionId, door) {
	const id = normalizeId(collectionId) || '';
	if (!/^[A-Za-z0-9_-]+$/.test(id)) return null;
	if (typeof door !== 'string' || !door.trim()) return null;
	const params = new URLSearchParams({ linked: '0', q: door });
	return `/collections/${id}/items?${params.toString()}`;
}
